import { useMemo } from 'react'
import HeroBriefCard from './HeroBriefCard.jsx'
import { useHeroBrief } from '../hooks/useHeroBrief.js'

// Daily Huddle hero brief. Phase 2 refactor (2026-05-10) — moved from
// the click-to-generate amber/blue tone-colored card to the charcoal
// hero pattern that every hub screen now uses.
//
// Two shape decisions worth calling out:
//
// 1. Bullets + recommended_action stay in the schema (richer than
//    other tabs). The model still emits them; HeroBriefCard renders
//    them on the charcoal background when present. Other tabs emit
//    just { headline, narrative } so their card is shorter.
//
// 2. The brief is now backed by the ai_briefs cache via useHeroBrief.
//    We still build a client-side snapshot from the data the Huddle
//    screen already has (no extra queries), and pass it to the edge
//    function on lazy fire. Cron path uses the same edge fn but
//    builds its snapshot server-side from restaurant_id.
export default function HuddleBriefCard({
  loading,
  todayWasteTotal,
  yesterdayTotal,
  todaySales,
  todayLabor,
  todayReceiveTotal,
  todayReceiveCount,
  todaySupplierCount,
  itemsNeedingReorder,
  criticalItems,
  restockCost,
  topWastedToday,
  todayAssignments,
  activeStaff,
  wastePctOfSales,
  laborPctOfSales,
  receivingsPctOfSales,
  primeCost7d,
  settings,
}) {
  // Compact snapshot for the AI. Only what's relevant to a morning
  // briefing — keeps tokens bounded.
  //
  // Important: useHuddleData exposes `todaySales` and `todayLabor` as
  // ROW OBJECTS, not numbers — todaySales = { net_sales, gross_sales,
  // transaction_count } from the daily_sales row. We dereference the
  // numeric fields explicitly here; Number({...}) collapses to NaN
  // and the AI then (correctly) reports "data is null".
  const snapshot = useMemo(() => {
    const today = new Date()
    const dayName = today.toLocaleDateString('en-US', { weekday: 'long' })
    const todaySalesNet   = Number(todaySales?.net_sales   ?? 0)
    const todaySalesGross = Number(todaySales?.gross_sales ?? 0)
    const todayLaborCost  = Number(todayLabor?.cost        ?? 0)
    const todayLaborHours = Number(todayLabor?.hours       ?? 0)
    return {
      date: today.toLocaleDateString('en-CA'),
      day_of_week: dayName,
      // Waste
      today_waste_total: Number(todayWasteTotal || 0),
      yesterday_waste_total: Number(yesterdayTotal || 0),
      daily_waste_target: Number(settings?.daily_target || 0),
      waste_pct_of_sales: wastePctOfSales,
      // useHuddleData returns topWastedToday as a single object (top-1)
      // OR null. Wrap as an array for the AI snapshot. The previous
      // (val || []).slice(0,3) crashed once topWastedToday was non-null.
      top_wasted_today: topWastedToday
        ? (Array.isArray(topWastedToday) ? topWastedToday.slice(0, 3) : [topWastedToday])
        : [],
      // Sales / labor
      today_sales_net: todaySalesNet,
      today_sales_gross: todaySalesGross,
      today_transaction_count: Number(todaySales?.transaction_count ?? 0),
      today_labor_cost: todayLaborCost,
      today_labor_hours: todayLaborHours,
      labor_pct_of_sales: laborPctOfSales,
      // Deliveries
      today_receive_total: Number(todayReceiveTotal || 0),
      today_receive_count: Number(todayReceiveCount || 0),
      today_supplier_count: Number(todaySupplierCount || 0),
      receivings_pct_of_sales: receivingsPctOfSales,
      // Inventory
      items_needing_reorder: Number(itemsNeedingReorder || 0),
      critical_item_count: Array.isArray(criticalItems) ? criticalItems.length : 0,
      restock_cost_estimate: Number(restockCost || 0),
      // Staffing
      shifts_scheduled_today: (todayAssignments || []).length,
      active_staff: Number(activeStaff || 0),
      // Prime cost (last 7 days)
      prime_cost_pct_7d: primeCost7d?.prime_cost_pct ?? null,
      food_cost_pct_7d:  primeCost7d?.food_cost_pct ?? null,
      labor_cost_pct_7d: primeCost7d?.labor_cost_pct ?? null,
    }
  }, [
    todayWasteTotal, yesterdayTotal, todaySales, todayLabor,
    todayReceiveTotal, todayReceiveCount, todaySupplierCount,
    itemsNeedingReorder, criticalItems, restockCost, topWastedToday,
    todayAssignments, activeStaff, wastePctOfSales, laborPctOfSales,
    receivingsPctOfSales, primeCost7d, settings,
  ])

  const hero = useHeroBrief('huddle', null, {
    snapshot,
    enabled: !loading,
  })

  return (
    <HeroBriefCard
      tabKey="huddle"
      phase={hero.phase}
      brief={hero.brief}
      error={hero.error}
      generatedAt={hero.generatedAt}
      onGenerate={hero.regenerate}
    />
  )
}
